/** Read-only formation planning shared by the pointer preview and queued orders. */
import type { Graphics } from 'pixi.js';
import { unitType } from '../data/units.ts';
import { clamp } from '../core/math.ts';
import { companyCommanded, Formation, OrderKind, type Company, type World } from '../sim/world.ts';
import { slotOffsetInto, spacingFor } from '../sim/systems/companies.ts';
import { Cmd, cmd, type Command } from '../sim/commands.ts';
import { MAP_W } from '../sim/constants.ts';
import type { Camera } from './camera.ts';

export interface FormationOrder { company: number; x: number; y: number; facing: number; width: number }
interface Bounds { x0: number; y0: number; x1: number; y1: number }
const GAP = 1.1;

export function formationSpacing(c: Company): number {
  return spacingFor(unitType(c.typeIdx).radius) * (c.formation === Formation.Loose ? 1.75 : 1);
}

export function formationBounds(c: Company, width = c.formationWidth): Bounds {
  const radius = unitType(c.typeIdx).radius + .12, spacing = formationSpacing(c), slot = { x: 0, y: 0 };
  const out = { x0: Infinity, y0: Infinity, x1: -Infinity, y1: -Infinity };
  for (let k = 0; k < Math.max(1, c.members.length); k++) {
    slotOffsetInto(c.formation, k, c.maxSize, spacing, width, slot);
    out.x0 = Math.min(out.x0, slot.x - radius); out.x1 = Math.max(out.x1, slot.x + radius);
    out.y0 = Math.min(out.y0, slot.y - radius); out.y1 = Math.max(out.y1, slot.y + radius);
  }
  return out;
}

/** Preserve lateral company order; give each its own frontage instead of stacking banners. */
export function planFormationMove(w: World, ids: readonly number[], x: number, y: number,
  drag?: { x: number; y: number }): FormationOrder[] {
  if (![x,y,drag?.x ?? 0,drag?.y ?? 0].every(Number.isFinite)) return [];
  const selected = new Set(ids);
  const companies = w.companies.filter(c => selected.has(c.id) && !c.dead && c.members.length && companyCommanded(w,c));
  if (!companies.length) return [];
  const cx = companies.reduce((sum,c) => sum + c.ax,0) / companies.length;
  const cy = companies.reduce((sum,c) => sum + c.ay,0) / companies.length;
  const length = drag ? Math.hypot(x - drag.x,y - drag.y) : 0;
  const dragging = !!drag && length > 1.2;
  const facing = dragging ? Math.atan2(x - drag!.x,drag!.y - y) :
    Math.hypot(x - cx,y - cy) > .05 ? Math.atan2(y - cy,x - cx) : companies[0].facing;
  const cos = Math.cos(facing), sin = Math.sin(facing);
  const targetX = dragging ? (drag!.x + x) / 2 : x;
  const targetY = dragging ? (drag!.y + y) / 2 : y;
  if (!dragging && companies.length === 1) return [{company:companies[0].id,
    x:clamp(x,1,w.map.w - 2),y:clamp(y,1,w.map.h - 2),facing,width:companies[0].formationWidth}];
  companies.sort((a,b) => (-a.ax * sin + a.ay * cos) - (-b.ax * sin + b.ay * cos) || a.id - b.id);
  const natural = companies.map(c => { const b = formationBounds(c); return b.y1 - b.y0; });
  const totalNatural = natural.reduce((a,b) => a + b,0);
  const available = Math.max(0,length - GAP * (companies.length - 1));
  const sizes = companies.map((c,i) => {
    const radius = unitType(c.typeIdx).radius + .12;
    const allotted = available * natural[i] / totalNatural;
    const width = dragging && c.formation !== Formation.Wedge
      ? clamp(Math.floor((allotted - radius * 2) / formationSpacing(c)) + 1,1,Math.min(c.maxSize,c.members.length))
      : c.formationWidth;
    return { c,width,bounds:formationBounds(c,width) };
  });
  const rows: typeof sizes[] = [[]];
  let rowSpan = 0;
  for (const size of sizes) {
    const span = size.bounds.y1 - size.bounds.y0;
    // Ordinary large-army moves keep a usable frontage; extra companies form behind it.
    if (!dragging && rowSpan > 0 && rowSpan + GAP + span > 48) { rows.push([]); rowSpan = 0; }
    rows[rows.length - 1].push(size); rowSpan += (rowSpan ? GAP : 0) + span;
  }
  const orders: FormationOrder[] = [];
  const sizeById = new Map(sizes.map(s => [s.c.id,s]));
  let rear = 0;
  for (const row of rows) {
    const span = row.reduce((sum,s) => sum + s.bounds.y1 - s.bounds.y0,0) + GAP * (row.length - 1);
    let side = -span / 2;
    let depth = 0;
    for (const s of row) {
      const lateral = side - s.bounds.y0;
      orders.push({ company:s.c.id, x:targetX + rear * cos - lateral * sin,
        y:targetY + rear * sin + lateral * cos, facing, width:s.width });
      side += s.bounds.y1 - s.bounds.y0 + GAP;
      depth = Math.max(depth,s.bounds.x1 - s.bounds.x0);
    }
    rear -= depth + GAP;
  }
  // Translate the whole layout at map edges, preserving the separation between companies.
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  for (const order of orders) {
    const b = sizeById.get(order.company)!.bounds;
    for (const [xx,yy] of [[b.x0,b.y0],[b.x1,b.y0],[b.x1,b.y1],[b.x0,b.y1]]) {
      const px = order.x + xx * cos - yy * sin, py = order.y + xx * sin + yy * cos;
      minX = Math.min(minX,px); maxX = Math.max(maxX,px); minY = Math.min(minY,py); maxY = Math.max(maxY,py);
    }
  }
  const shiftX = minX < 1 ? 1 - minX : maxX > w.map.w - 2 ? w.map.w - 2 - maxX : 0;
  const shiftY = minY < 1 ? 1 - minY : maxY > w.map.h - 2 ? w.map.h - 2 - maxY : 0;
  for (const order of orders) { order.x += shiftX; order.y += shiftY; }
  return orders;
}

/** Raw world coordinates are the command protocol; no screen/fixed-point conversion. */
export function formationMoveCommands(orders: readonly FormationOrder[], attackMove: boolean, setFacing: boolean): Command[] {
  return orders.flatMap(o => [cmd(Cmd.AttackCompany,o.company,-1),
    cmd(Cmd.MoveCompany,o.company,o.x,o.y,attackMove ? 1 : 0),
    ...(setFacing ? [cmd(Cmd.SetFacing,o.company,o.facing,o.width)] : [])]);
}

export function focusedAttackCommands(ids: readonly number[], foe: number, x: number, y: number): Command[] {
  return ids.flatMap(id => [cmd(Cmd.AttackCompany,id,foe),cmd(Cmd.MoveCompany,id,x,y,1)]);
}

export function drawFormationFootprint(g: Graphics, cam: Camera, c: Company, order: FormationOrder, color: number, alpha: number): void {
  const z = cam.effectiveZoom, b = formationBounds(c,order.width), cos = Math.cos(order.facing), sin = Math.sin(order.facing);
  const p = (x: number,y: number): [number,number] =>
    [cam.worldToScreenX(order.x + x * cos - y * sin),cam.worldToScreenY(order.y + x * sin + y * cos)];
  const corners = [p(b.x0,b.y0),p(b.x1,b.y0),p(b.x1,b.y1),p(b.x0,b.y1)];
  if (corners.every(([x]) => x < -z) || corners.every(([x]) => x > cam.viewW + z) ||
      corners.every(([,y]) => y < -z) || corners.every(([,y]) => y > cam.viewH + z)) return;
  g.poly(corners.flat()).fill({color,alpha:alpha * .07}).stroke({color,width:1.2,alpha:alpha * .48});
  const slot = {x:0,y:0}, spacing = formationSpacing(c);
  for (let k = 0; k < c.members.length; k += Math.max(1,Math.ceil(c.members.length / 100))) {
    slotOffsetInto(c.formation,k,c.maxSize,spacing,order.width,slot);
    const [x,y] = p(slot.x,slot.y);
    g.ellipse(x,y,Math.max(1.5,z * .1),Math.max(1,z * .065)).fill({color,alpha:alpha * .62});
  }
  const middle = (b.y0 + b.y1) / 2;
  g.moveTo(...p(b.x1 + .22,middle - .35)).lineTo(...p(b.x1 + .72,middle))
    .lineTo(...p(b.x1 + .22,middle + .35)).stroke({color,width:2,alpha});
}

/** Draw the route actually assigned to the banner, including its crossing waypoints. */
export function drawCompanyOrder(g: Graphics, cam: Camera, c: Company): void {
  if (c.orderKind === OrderKind.None && c.orderFacing == null && c.cohesion >= .98) return;
  const color = c.orderKind === OrderKind.AttackMove ? 0xd7966a : 0xd5c489;
  const points = [[c.ax,c.ay],...c.route.slice(0,192).map(k => [k % MAP_W + .5,Math.floor(k / MAP_W) + .5])];
  if (c.route.length <= 192) points.push([c.orderX,c.orderY]);
  const view = cam.viewBounds(1);
  for (const [width,alpha,col] of [[4,.45,0x20251d],[1.6,.7,color]]) {
    let connected = false;
    for (let i = 1; i < points.length; i++) {
      const a = points[i - 1], b = points[i];
      if (Math.max(a[0],b[0]) < view.x0 || Math.min(a[0],b[0]) > view.x1 ||
          Math.max(a[1],b[1]) < view.y0 || Math.min(a[1],b[1]) > view.y1) { connected = false; continue; }
      if (!connected) g.moveTo(cam.worldToScreenX(a[0]),cam.worldToScreenY(a[1]));
      g.lineTo(cam.worldToScreenX(b[0]),cam.worldToScreenY(b[1])); connected = true;
    }
    g.stroke({width,color:col,alpha});
  }
  drawFormationFootprint(g,cam,c,{company:c.id,x:c.orderX,y:c.orderY,
    facing:c.orderFacing ?? c.facing,width:c.formationWidth},color,.8);
}
